import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { useQueryClient } from "@tanstack/react-query";
import { updateCity, updateCountry } from "../users/users-service";
import { UpdatableNameTextField } from "./UpdatableNameTextField";
import { ThisUser } from "../games/query-keys";
import { useMyProfile, useUpdateProfile } from "@seabrookstudios/pitch2table";
import { CurrentDropDown } from "../currency/CurrencyDropDown";
import { enqueueSnackbar } from "notistack";
import { useUpdatePreferredCurrency } from "@seabrookstudios/pitch2table";
import { TimezoneSelect } from "../core/TimezoneDropDown";

const CityTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="City"
      id="city"
      helperText="The city where you reside."
      initialValue={initialValue}
      updateFunction={updateCity}
      queryKey={ThisUser()}
    />
  );
};

const CountryTextField = ({ initialValue }) => {
  return (
    <UpdatableNameTextField
      label="Country"
      id="country"
      helperText="The country you live in. This is relevant to some publishers."
      initialValue={initialValue}
      updateFunction={updateCountry}
      queryKey={ThisUser()}
    />
  );
};

const PreferredCurrency = ({ initialValue }) => {
  const queryClient = useQueryClient();

  const onSuccess = () => {
    enqueueSnackbar(`Preferred currency updated.`, { variant: "success" });
    queryClient.invalidateQueries({ queryKey: ThisUser() });
  };

  const updatePreferredCurrency = useUpdatePreferredCurrency(onSuccess);

  const saveCurrency = async (newValue) => {
    await updatePreferredCurrency.mutate(newValue);
  };

  return (
    <CurrentDropDown
      label="Preferred Currency"
      value={initialValue}
      onChange={saveCurrency}
      helperText="All amounts will be converted to this for display. The original currency of any royalty payment will not be affected."
    />
  );
};

/**
 * @param {object} props
 * @param {string} props.initialValue
 */
const TimeZoneDropDown = ({ initialValue }) => {
  const updateProfile = useUpdateProfile();

  /**
   * @param {string} newValue
   */
  const onChange = async (newValue) => {
    await updateProfile.mutateAsync({
      toUpdate: {
        timezone: newValue,
        firstName: undefined,
        lastName: undefined,
        preferredName: undefined,
        pronouns: undefined,
        contactNumber: undefined,
        city: undefined,
        country: undefined,
        marketingConsent: undefined,
        sendDailyEmails: undefined,
        sendNotifications: undefined,
        whenToSendDailyEmails: undefined,
        preferredCurrency: undefined,
        slug: undefined,
        isPublic: undefined,
        showEmail: undefined,
      },
    });
  };

  return (
    <TimezoneSelect
      value={initialValue}
      onChange={onChange}
      title="Time Zone"
      helperText="The timezone you live in. This is relevant to most publishers. We also use this when scheduling emails for you."
    />
  );
};

export const YourLocation = () => {
  const { data: profile, isLoading } = useMyProfile();

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <span>An error occurred, please refresh the page.</span>
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <CityTextField initialValue={profile.location.city} />
        </ListItem>
        <ListItem>
          <CountryTextField initialValue={profile.location.country} />
        </ListItem>
        <ListItem>
          <TimeZoneDropDown initialValue={profile.location.timezone} />
        </ListItem>
        <ListItem>
          <PreferredCurrency initialValue={profile.currencySettings.preferredCurrency} />
        </ListItem>
      </List>
    </Card>
  );
};
